import {
  ImputableProjectForSelectFragment,
  MyProjectsScheduleEmployeeFragment,
  MyProjectsScheduleProjectWorkerFragment,
  MyScheduleDocument,
  MyScheduleQuery,
  ProjectForSelectFragment,
} from 'generated/resources/graphql'
import moment, { Moment } from 'moment'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ScheduleParseDataType } from 'modules/project_management/components/Schedule/Body'
import { ScheduleRecordType } from 'modules/project_management/components/Schedule/Body/ScheduleItem/types'
import { FooterElement } from 'modules/project_management/components/Schedule/Footer/index'
import Schedule from 'modules/project_management/components/Schedule/index'

const MySchedule = () => {
  useEffect(() => {
    track(trackingEvents.projectManagement.flexibleTracking.visitMyProjectsScheduleSection, {})
  }, [])

  const { employee: currentEmployee } = useCurrentSession()

  const dataSource = (data: MyScheduleQuery) => {
    const connection = data?.projectManagement.projectWorkersConnection
    return {
      edges:
        connection?.edges.map((edge) => ({
          cursor: edge.cursor ?? '',
          node: edge.node,
        })) ?? [],
      pageInfo: {
        hasNextPage: connection?.pageInfo.hasNextPage ?? false,
        endCursor: connection?.pageInfo.endCursor ?? '',
      },
    }
  }

  const projectToSelectFragment = (
    imputableProject: ImputableProjectForSelectFragment
  ): ProjectForSelectFragment => {
    return {
      id: imputableProject.id,
      name: imputableProject.name,
      code: imputableProject.code,
      subprojectsConnection: imputableProject.subprojectsConnection,
    }
  }

  const parseData = (
    data: MyScheduleQuery | undefined,
    startDate: Moment,
    endDate: Moment
  ): ScheduleParseDataType => {
    const transformRecords = (
      projectWorker: MyProjectsScheduleProjectWorkerFragment,
      employee: EmployeeForSelectFragmentNoAccess
    ): ScheduleRecordType => {
      return projectWorker.plannedRecords.reduce<ScheduleRecordType>((acc, record) => {
        const key = record.subproject?.id ? record.subproject?.id?.toString() : 'null'

        if (!acc[key]) {
          acc[key] = []
        }

        if (
          projectWorker.imputableProject &&
          moment(record.startDate).isBefore(endDate) &&
          moment(record.endDate).isAfter(startDate)
        ) {
          acc[key].push({
            id: record.id,
            startDate: record.startDate,
            endDate: record.endDate,
            dailyMinutes: record.dailyMinutes,
            group: projectWorker.imputableProject?.name ?? '',
            title: record.subproject ? record.subproject.name : null,
            subtitle: projectWorker.imputableProject?.client?.legalName ?? null,
            projectWorkerId: projectWorker.id,
            project: projectToSelectFragment(projectWorker.imputableProject),
            subproject: record.subproject ?? undefined,
            employee,
          })
        }

        if (acc[key].length === 0) {
          delete acc[key]
        }

        return acc
      }, {})
    }

    if (!data?.employees.employee) return { items: [], totalCount: 0 }

    const employee = data.employees.employee

    const employeeToSelectFragment = (
      employeeSelect: MyProjectsScheduleEmployeeFragment
    ): EmployeeForSelectFragmentNoAccess => {
      return {
        id: employeeSelect.id,
        fullName: employeeSelect.fullName,
        firstName: employeeSelect.firstName,
        lastName: employeeSelect.lastName,
        access: { avatar: null },
      }
    }

    const employeeToSelect = employeeToSelectFragment(employee)

    const projectWorkers =
      data.projectManagement.projectWorkersConnection.edges.map((edge) => edge.node) || []

    return {
      items: [
        {
          headerRow: {
            id: parseInt(employee.id.toString() || '0'),
            fullName: employee.fullName || '',
            avatar: employee.avatar?.url || '',
            jobTitle: employee.referenceContract?.jobTitle || '',
          },
          employeeToSelect,
          rows: projectWorkers
            .map((pw: MyProjectsScheduleProjectWorkerFragment) => {
              const records = transformRecords(pw, employeeToSelect)
              if (Object.keys(records).length === 0) return null

              return {
                firstColumn: {
                  id: pw.imputableProject?.id.toString() || '',
                  title: pw.imputableProject?.name || '',
                  subtitle: pw.imputableProject?.client?.legalName || '',
                },
                records,
              }
            })
            .filter((row) => row !== null),
        },
      ],
      totalCount: 0,
    }
  }

  const footerElementsList: FooterElement[] = [
    {
      color: 'purple200',
      circleType: 'circle',
      description: i18n.t(
        'project_management.schedule_contributors.footer_legend.total_assigned_hours'
      ),
    },
    {
      color: 'purple1000',
      circleType: 'circle-line',
      description: i18n.t(
        'project_management.schedule_contributors.footer_legend.detailed_hours_per_project'
      ),
    },
  ]

  return (
    currentEmployee && (
      <Schedule
        document={MyScheduleDocument}
        documentVariables={{
          employeeId: currentEmployee.id,
          startDate: '2025-03-01',
          endDate: '2025-03-29',
        }}
        dataSource={dataSource}
        parseData={parseData}
        footerLegend={footerElementsList}
        isMyProjectsView
      />
    )
  )
}

export default MySchedule
